<template>
  <v-tooltip
    ref="tooltipRef"
    :bottom="placement === 'bottom'"
    :top="placement === 'top'"
    :left="placement === 'left'"
    :right="placement === 'right'"
    :disabled="disabled"
    :eager="resolvedEager"
    :transition="resolvedTransition"
    :open-delay="positioned ? 0 : openDelay"
    :content-class="resolvedContentClass"
    :value="positioned ? value : undefined"
    :position-x="positioned ? positionX : undefined"
    :position-y="positioned ? positionY : undefined"
    :absolute="positioned"
    :fixed="positioned || fixed"
    @input="onTooltipInput"
  >
    <template v-if="!positioned" v-slot:activator="{ on, attrs }">
      <span
        class="fc-tooltip__activator"
        :class="[activatorClass, { 'fc-tooltip__activator--focusable': focusable }]"
        v-bind="attrs"
        :tabindex="focusable ? 0 : null"
        v-on="on"
      >
        <slot />
      </span>
    </template>

    <div
      v-if="$slots.content"
      class="fc-tooltip__content"
      :data-testid="dataTestid"
    >
      <slot name="content" />
    </div>
    <span v-else-if="text" :data-testid="dataTestid">{{ text }}</span>
  </v-tooltip>
</template>

<script>
import { repositionVTooltip } from '@/views/common/tooltipPosition';

export default {
  name: 'FcTooltip',

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    openDelay: {
      type: [Number, String],
      default: 300,
    },
    placement: {
      type: String,
      default: 'bottom',
      validator: (value) => ['bottom', 'top', 'left', 'right'].includes(value),
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'rich'].includes(value),
    },
    text: {
      type: String,
      default: '',
    },
    dataTestid: {
      type: String,
      default: 'fc-tooltip',
    },
    activatorClass: {
      type: [String, Array, Object],
      default: '',
    },
    /** Programmatic tooltip anchored to viewport coordinates (no activator). */
    positioned: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: false,
    },
    positionX: {
      type: [Number, String],
      default: 0,
    },
    positionY: {
      type: [Number, String],
      default: 0,
    },
    /** Extra classes merged onto the teleported tooltip content element. */
    contentClass: {
      type: String,
      default: '',
    },
    /** Viewport positioning — set in overflow:auto tables so the bubble is not clipped. */
    fixed: {
      type: Boolean,
      default: false,
    },
    /** Render the bubble before the first hover so its width can be measured. */
    eager: {
      type: Boolean,
      default: false,
    },
    /** Vuetify transition name. Fade avoids the scale animation measuring a zero width. */
    transition: {
      type: String,
      default: '',
    },
    /**
     * Puts the activator in tab order so focus and blur open and close the tooltip.
     * Vuetify listens on the activator; a plain span never receives those events.
     */
    focusable: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    onTooltipInput(isOpen) {
      if (!isOpen || this.positioned) return;
      repositionVTooltip(this.$refs.tooltipRef);
    },
  },

  computed: {
    tooltipContentClass() {
      const classes = ['fc-tooltip', `tooltip-${this.placement}`];

      if (this.variant === 'rich') {
        classes.push('fc-tooltip--rich');
      }

      return classes.join(' ');
    },
    resolvedContentClass() {
      if (!this.contentClass) return this.tooltipContentClass;
      return `${this.tooltipContentClass} ${this.contentClass}`;
    },
    resolvedEager() {
      return this.eager || this.variant === 'rich';
    },
    resolvedTransition() {
      if (this.transition) return this.transition;
      return this.variant === 'rich' ? 'fade-transition' : undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
.fc-tooltip__activator {
  display: inline-flex;
  line-height: 0;

  &--focusable:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
  }
}
</style>

<style lang="scss">
@import './FcTooltip.scss';
</style>
